import React from 'react';
import { Box, Typography, Paper, Chip, Tooltip, IconButton, Checkbox, Pagination, CircularProgress } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';

const PRIMARY = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

/** Linha de notícia relacionada — selecionável via checkbox, com acesso à matéria completa */
export const RelatedNewsRow = ({ item, checked, onToggle }) => (
    <Box
        onClick={() => onToggle(item.id)}
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5,
            p: '8px 10px',
            borderRadius: '8px',
            cursor: 'pointer',
            border: `1px solid ${checked ? PRIMARY : '#eee'}`,
            backgroundColor: checked ? `${PRIMARY}08` : '#fff',
            transition: 'all 0.12s',
            '&:hover': { borderColor: PRIMARY, backgroundColor: `${PRIMARY}06` },
        }}
    >
        <Checkbox
            checked={checked}
            onChange={() => onToggle(item.id)}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ p: 0.4, mt: '-2px', color: '#ccc', '&.Mui-checked': { color: PRIMARY } }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Chip
                label={item.source}
                size="small"
                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', mb: 0.5 }}
            />
            <Typography sx={{ fontSize: '0.8rem', color: '#333', lineHeight: 1.4 }}>
                {item.title}
            </Typography>
        </Box>
        <Tooltip title="Acessar matéria completa" arrow>
            <span>
                <IconButton
                    size="small"
                    disabled={!item.url}
                    onClick={(e) => {
                        e.stopPropagation();
                        window.open(item.url, '_blank', 'noopener,noreferrer');
                    }}
                    sx={{ color: '#999', mt: '-2px', '&:hover': { color: PRIMARY, backgroundColor: `${PRIMARY}10` } }}
                >
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </span>
        </Tooltip>
    </Box>
);

/**
 * Painel de "Notícias relacionadas" — inteligências editoriais relacionadas
 * a uma inteligência editorial, selecionáveis via checkbox e usadas como
 * contexto opcional na escrita da matéria. Análogo ao `NeoFeedSuggestionsPanel`,
 * paginado no servidor (5 por página).
 *
 * @param {{
 *   embedded?: boolean;
 *   items: Array<{ id: string|number, title: string, source: string, url?: string }>;
 *   total: number;
 *   loading: boolean;
 *   page: number;
 *   pageSize?: number;
 *   onPageChange: (page: number) => void;
 *   selectedIds: Array<string|number>;
 *   onToggle: (id: string|number) => void;
 *   emptyMessage?: string;
 * }} props
 */
export default function RelatedNewsPanel({
    embedded = false,
    items = [],
    total = 0,
    loading = false,
    page = 0,
    pageSize = 5,
    onPageChange,
    selectedIds = [],
    onToggle,
    emptyMessage = 'Nenhuma notícia relacionada encontrada.',
}) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const rangeStart = total === 0 ? 0 : page * pageSize + 1;
    const rangeEnd = Math.min((page + 1) * pageSize, total);

    const content = (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {selectedIds.length > 0 && (
                <Box sx={{ px: embedded ? 0 : 1.3, pt: embedded ? 0 : 1.2, pb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>
                        {`${selectedIds.length} selecionada${selectedIds.length > 1 ? 's' : ''} como contexto`}
                    </Typography>
                </Box>
            )}

            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                p: embedded ? '2px 2px' : '8px 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.8,
                minHeight: 0,
            }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={20} sx={{ color: PRIMARY }} />
                    </Box>
                )}

                {!loading && items.length === 0 && (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <NewspaperOutlinedIcon sx={{ fontSize: 28, color: '#e0e0e0', mb: 0.8 }} />
                        <Typography sx={{ color: '#bbb', fontSize: '0.78rem' }}>
                            {emptyMessage}
                        </Typography>
                    </Box>
                )}

                {!loading && items.map((news) => (
                    <RelatedNewsRow
                        key={news.id}
                        item={news}
                        checked={selectedIds.includes(news.id)}
                        onToggle={onToggle}
                    />
                ))}
            </Box>

            {!loading && total > 0 && (
                <Box sx={{
                    p: '10px 14px',
                    borderTop: '1px solid #f0f0f0',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                }}>
                    <Typography sx={{ fontSize: '0.7rem', color: '#999' }}>
                        {rangeStart}–{rangeEnd} de {total}
                    </Typography>
                    <Pagination
                        count={totalPages}
                        page={page + 1}
                        onChange={(_, p) => onPageChange(p - 1)}
                        size="small"
                        siblingCount={0}
                        boundaryCount={1}
                        sx={{
                            '& .MuiPaginationItem-root.Mui-selected': {
                                backgroundColor: PRIMARY,
                                color: '#fff',
                                '&:hover': { backgroundColor: PRIMARY_DARK },
                            },
                        }}
                    />
                </Box>
            )}
        </Box>
    );

    if (embedded) return content;

    return (
        <Paper sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            border: '1px solid #f0f0f0',
            minHeight: 0,
            flex: 1,
        }}>
            {content}
        </Paper>
    );
}
